import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AdminSidebar from "../components/layout/AdminSidebar";
import NotificationToggle from "../components/NotificationToggle";
import { toast } from "sonner";
import {
  FileText, AlertTriangle, Users, ChevronRight, Building2,
  ClipboardCheck, Bot, DollarSign, Download, TrendingUp, TrendingDown,
  Shield, Eye, Camera, BarChart3, Calendar, Loader2,
  CheckCircle, Clock, XCircle, ArrowUpRight, Sparkles, Activity,
  Target, Lightbulb, Zap, ArrowDown, ArrowUp, Minus
} from "lucide-react";

/* ── tiny bar chart (pure CSS) ─────────────────────────────── */
const MiniBarChart = ({ data, color = "bg-primary", height = 80 }) => {
  if (!data?.length) return <p className="text-xs text-muted-foreground text-center py-4">No data for this period</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-[3px] w-full" style={{ height }} data-testid="mini-bar-chart">
      {data.map((d, i) => (
        <div key={i} className="flex-1 group relative flex flex-col items-center justify-end">
          <div
            className={`w-full rounded-t ${color} opacity-80 group-hover:opacity-100 transition-opacity min-h-[2px]`}
            style={{ height: `${Math.max((d.count / max) * 100, 3)}%` }}
          />
          <span className="absolute -top-5 text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            {d.count}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ── category bar ──────────────────────────────────────────── */
const CategoryBar = ({ categories, max }) => {
  if (!categories?.length) return <p className="text-xs text-muted-foreground">No data</p>;
  const topMax = categories[0]?.count || 1;
  return (
    <div className="space-y-2">
      {categories.slice(0, 5).map((c, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs w-24 truncate capitalize text-muted-foreground">{c.category}</span>
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary/70 rounded-full transition-all" style={{ width: `${(c.count / topMax) * 100}%` }} />
          </div>
          <span className="text-xs font-medium w-6 text-right">{c.count}</span>
        </div>
      ))}
    </div>
  );
};

/* ── delta badge ───────────────────────────────────────────── */
const DeltaBadge = ({ value }) => {
  if (value === 0) return <Badge variant="outline" className="text-xs gap-1"><Minus className="w-3 h-3" />0%</Badge>;
  if (value > 0) return <Badge variant="outline" className="text-xs gap-1 text-rose-600 border-rose-200 bg-rose-50"><ArrowUp className="w-3 h-3" />{value}%</Badge>;
  return <Badge variant="outline" className="text-xs gap-1 text-emerald-600 border-emerald-200 bg-emerald-50"><ArrowDown className="w-3 h-3" />{Math.abs(value)}%</Badge>;
};

/* ── health score ring ─────────────────────────────────────── */
const HealthRing = ({ score, label }) => {
  const color = score >= 80 ? "text-emerald-500" : score >= 60 ? "text-blue-500" : score >= 40 ? "text-amber-500" : "text-rose-500";
  const bgColor = score >= 80 ? "stroke-emerald-500" : score >= 60 ? "stroke-blue-500" : score >= 40 ? "stroke-amber-500" : "stroke-rose-500";
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2" data-testid="health-score-ring">
      <div className="relative w-28 h-28">
        <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/30" />
          <circle cx="50" cy="50" r="40" fill="none" strokeWidth="6" strokeLinecap="round"
            className={bgColor} strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-2xl font-bold ${color}`}>{score}</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Score</span>
        </div>
      </div>
      <Badge variant="outline" className={`${color} text-xs`}>{label}</Badge>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
const BoardDashboard = () => {
  const { user, token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [period, setPeriod] = useState(30);

  const fetchData = useCallback(async (days) => {
    setLoading(true);
    try {
      const [dataRes, reportRes] = await Promise.all([
        axios.get(`${API}/ai-analytics/data?period_days=${days}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/board-reports/summary?period_days=${days}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setAnalytics(dataRes.data?.analytics || null);
      setReportData(reportRes.data);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const fetchAiInsights = async () => {
    setAiLoading(true);
    try {
      const res = await axios.get(`${API}/ai-analytics/dashboard?period_days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 60000
      });
      setAiInsights(res.data.ai_insights);
      if (res.data.analytics) setAnalytics(res.data.analytics);
      toast.success("AI insights generated");
    } catch { toast.error("Failed to generate AI insights"); }
    finally { setAiLoading(false); }
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API}/board-reports/generate?period_days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `HMR_Board_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
    finally { setPdfLoading(false); }
  };

  const v = analytics?.violations || {};
  const d = analytics?.drc || {};
  const c = analytics?.complaints || {};
  const f = analytics?.financial || {};
  const cm = analytics?.community || {};

  return (
    <AdminSidebar>
      <div className="p-4 lg:p-8 max-w-[1400px] mx-auto" data-testid="board-dashboard">
        {/* ── header ─────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Board Dashboard</h1>
            <p className="text-sm text-muted-foreground">Welcome, {user?.name}. Analytics for the last {period} days.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Period selector */}
            <div className="flex border rounded-lg overflow-hidden" data-testid="period-selector">
              {[30, 60, 90, 180].map(p => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${period === p ? "bg-primary text-white" : "hover:bg-muted"}`}
                  data-testid={`period-${p}`}
                >{p}d</button>
              ))}
            </div>
            <NotificationToggle compact />
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={pdfLoading} data-testid="download-report-btn">
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline ml-2">PDF</span>
            </Button>
            <Button size="sm" onClick={fetchAiInsights} disabled={aiLoading} data-testid="generate-ai-btn">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              <span className="hidden sm:inline ml-2">AI Insights</span>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* ── AI Executive Summary ─────────────────────── */}
            {aiInsights && (
              <Card className="mb-6 border-violet-200/60 bg-gradient-to-br from-violet-50/40 to-background" data-testid="ai-executive-card">
                <CardContent className="pt-5 pb-4">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Health score ring */}
                    <div className="flex-shrink-0 flex justify-center">
                      <HealthRing score={aiInsights.health_score || 50} label={aiInsights.health_label || "Fair"} />
                    </div>

                    {/* Summary & Trends */}
                    <div className="flex-1 min-w-0 space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider flex items-center gap-1 mb-1"><Sparkles className="w-3 h-3" /> AI Executive Summary</p>
                        <p className="text-sm leading-relaxed">{aiInsights.executive_summary}</p>
                      </div>
                      {aiInsights.key_trends?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Key Trends</p>
                          <div className="flex flex-wrap gap-1.5">
                            {aiInsights.key_trends.map((t, i) => (
                              <Badge key={i} variant="outline" className="text-xs font-normal">{t}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Recommendations */}
                    {aiInsights.recommendations?.length > 0 && (
                      <div className="lg:w-72 flex-shrink-0 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Recommendations</p>
                        {aiInsights.recommendations.slice(0, 3).map((r, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <Badge variant="outline" className={`flex-shrink-0 text-[10px] mt-0.5 ${r.priority === "high" ? "text-rose-600 border-rose-200" : r.priority === "medium" ? "text-amber-600 border-amber-200" : "text-blue-600 border-blue-200"}`}>
                              {r.priority}
                            </Badge>
                            <span>{r.action}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── stat cards ────────────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              {[
                { icon: <Building2 className="w-4 h-4" />, label: "Properties", value: cm.total_properties || 0, color: "text-blue-600", bg: "bg-blue-50" },
                { icon: <Users className="w-4 h-4" />, label: "Owners", value: cm.total_owners || 0, sub: `${cm.verified || 0} verified`, color: "text-emerald-600", bg: "bg-emerald-50" },
                { icon: <AlertTriangle className="w-4 h-4" />, label: "Open Notices", value: v.open || 0, delta: v.delta_pct, color: "text-rose-600", bg: "bg-rose-50" },
                { icon: <ClipboardCheck className="w-4 h-4" />, label: "Pending DRC", value: d.pending || 0, delta: d.delta_pct, color: "text-amber-600", bg: "bg-amber-50" },
                { icon: <FileText className="w-4 h-4" />, label: "Open Complaints", value: c.open || 0, delta: c.delta_pct, color: "text-indigo-600", bg: "bg-indigo-50" },
              ].map((s, i) => (
                <Card key={i} className="border-border/50" data-testid={`board-stat-${i}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                        <p className="text-xl font-bold mt-0.5">{s.value}</p>
                        {s.sub && <p className="text-[10px] text-muted-foreground">{s.sub}</p>}
                        {s.delta !== undefined && <DeltaBadge value={s.delta} />}
                      </div>
                      <div className={`w-8 h-8 ${s.bg} rounded-lg flex items-center justify-center ${s.color}`}>{s.icon}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* ── trend charts & breakdowns ─────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Violations trend */}
              <Card data-testid="violations-trend-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-500" /> Violations Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MiniBarChart data={v.trend} color="bg-rose-400" />
                  <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                    <span>{v.trend?.[0]?.week}</span><span>{v.trend?.[v.trend?.length - 1]?.week}</span>
                  </div>
                </CardContent>
              </Card>

              {/* DRC trend */}
              <Card data-testid="drc-trend-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-amber-500" /> DRC Submissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MiniBarChart data={d.trend} color="bg-amber-400" />
                  <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                    <span>{d.trend?.[0]?.week}</span><span>{d.trend?.[d.trend?.length - 1]?.week}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Complaints trend */}
              <Card data-testid="complaints-trend-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-500" /> Complaints
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MiniBarChart data={c.trend} color="bg-indigo-400" />
                  <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                    <span>{c.trend?.[0]?.week}</span><span>{c.trend?.[c.trend?.length - 1]?.week}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ── breakdowns, financial, risk ───────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Violation categories */}
              <Card data-testid="violation-categories-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Violation Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <CategoryBar categories={v.categories} />
                </CardContent>
              </Card>

              {/* Financial */}
              <Card data-testid="financial-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" /> Financial
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Collected</p>
                    <p className="text-xl font-bold text-emerald-600">${(f.collected || 0).toLocaleString()}</p>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Pending</p>
                    <p className="text-xl font-bold text-amber-600">${(f.pending || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Verification Rate</p>
                    <div className="flex items-center gap-2">
                      <Progress value={cm.verification_rate || 0} className="h-2 flex-1" />
                      <span className="text-xs font-medium">{cm.verification_rate || 0}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Risk / Predictions */}
              <Card data-testid="risk-predictions-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-violet-600" /> Risk & Predictions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {aiInsights ? (
                    <>
                      {aiInsights.risk_factors?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase mb-1">Risk Factors</p>
                          {aiInsights.risk_factors.map((r, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs mb-1">
                              <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                              <span>{r}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <Separator />
                      {aiInsights.predictions?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase mb-1">Predictions</p>
                          {aiInsights.predictions.map((p, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs mb-1">
                              <Zap className="w-3 h-3 text-violet-500 mt-0.5 flex-shrink-0" />
                              <span>{p}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-xs text-muted-foreground mb-2">Click "AI Insights" to generate risk analysis</p>
                      <Button size="sm" variant="outline" onClick={fetchAiInsights} disabled={aiLoading} data-testid="generate-ai-inline-btn">
                        {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        Generate
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Quick actions + recent activity ──────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Quick actions */}
              <Card className="lg:col-span-1" data-testid="quick-actions-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                  {[
                    { icon: <AlertTriangle className="w-4 h-4" />, title: "Notices", href: "/board/violations", c: "text-rose-600" },
                    { icon: <ClipboardCheck className="w-4 h-4" />, title: "DRC", href: "/board/drc", c: "text-amber-600" },
                    { icon: <FileText className="w-4 h-4" />, title: "Documents", href: "/board/documents", c: "text-blue-600" },
                    { icon: <Camera className="w-4 h-4" />, title: "Reports", href: "/board/community-reports", c: "text-purple-600" },
                    { icon: <Users className="w-4 h-4" />, title: "Owners", href: "/board/properties", c: "text-emerald-600" },
                    { icon: <Bot className="w-4 h-4" />, title: "AI Chat", href: "/board/ai", c: "text-violet-600" },
                  ].map((a, i) => (
                    <Link key={i} to={a.href}>
                      <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors" data-testid={`qa-${i}`}>
                        <span className={a.c}>{a.icon}</span>
                        <span className="text-xs font-medium">{a.title}</span>
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>

              {/* Recent activity */}
              <Card className="lg:col-span-3" data-testid="recent-activity-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-muted-foreground" /> Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="violations">
                    <TabsList className="h-8">
                      <TabsTrigger value="violations" className="text-xs">Notices</TabsTrigger>
                      <TabsTrigger value="drc" className="text-xs">DRC</TabsTrigger>
                    </TabsList>
                    <TabsContent value="violations" className="mt-2">
                      {reportData?.violations?.recent?.length > 0 ? (
                        <div className="space-y-1.5">
                          {reportData.violations.recent.slice(0, 6).map((item, i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/40 text-xs">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[10px] capitalize">{item.category || "N/A"}</Badge>
                                <span className="text-muted-foreground">{item.created_at?.slice(0, 10)}</span>
                              </div>
                              <Badge variant="outline" className="text-[10px] capitalize">{item.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-xs text-muted-foreground text-center py-4">No recent notices</p>}
                    </TabsContent>
                    <TabsContent value="drc" className="mt-2">
                      {reportData?.drc?.recent?.length > 0 ? (
                        <div className="space-y-1.5">
                          {reportData.drc.recent.slice(0, 6).map((item, i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/40 text-xs">
                              <span className="font-medium">{item.project_type || "N/A"}</span>
                              <Badge variant="outline" className="text-[10px] capitalize">{item.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-xs text-muted-foreground text-center py-4">No recent DRC submissions</p>}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminSidebar>
  );
};

export default BoardDashboard;
